import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { Icon } from "@/components/Icon";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import LiveDealerVideo from "./LiveDealerVideo";
import useBlackjackTable from "./useBlackjackTable";
import SharedPlayingCard from "@/components/PlayingCard";
import { LIVE_BLACKJACK_TABLES, handResultLabel, decisionLabel, visibleStateDelayMs } from "./tableConfig";

/* Immersive Live Blackjack — dealer table (design "08 Live Blackjack — Dealer Table", LB-01/02/M1/M2).
   Full-bleed WHEP dealer video hero → gradient blend → docked control tray. All game/money logic,
   seat join/leave, betting, side bets, actions, settlement, insurance, stale-pause and the
   visibleStateDelayMs card-sync withholding are preserved exactly; this is a visual reskin only. */

/* Thin wrapper over the shared premium <PlayingCard> so every card surface in the
   product renders identically. Local size tiers map to the shared component's
   presets: small (seats) → xs, default (dealer felt) → sm, hero (Your-Hand band) → lg.
   Face-down / the "?" sentinel maps to the shared card-back. All call sites and their
   small/hero/style props are preserved. */
function PlayingCard({ card, small = false, hero = false, style }) {
  const size = hero ? "lg" : small ? "xs" : "sm";
  const faceDown = !card || card.rank === "?";
  return <SharedPlayingCard card={card} size={size} faceDown={faceDown} style={style} />;
}

// Small 3-chip stack visual for the bets summary (edge spots + inner ring, green fill, no glow).
function ChipStack({ label }) {
  const chipCls =
    "relative h-7 w-7 rounded-full border border-black/30 bg-[radial-gradient(circle_at_50%_38%,#1cf24d_0%,#00D632_58%,#00a827_100%)] shadow-[0_2px_4px_rgba(0,0,0,0.5)] before:absolute before:inset-[3px] before:rounded-full before:border before:border-dashed before:border-white/50 before:content-['']";
  return (
    <span className="relative inline-flex h-7 w-9 items-center" aria-hidden data-testid="bjv2-chipstack">
      <span className={`${chipCls} absolute left-0 top-0`} />
      <span className={`${chipCls} absolute left-1 top-[-3px]`} />
      <span className={`${chipCls} absolute left-2 top-[-6px]`}>
        {label ? <span className="absolute inset-0 flex items-center justify-center text-[8px] font-black text-black">{label}</span> : null}
      </span>
    </span>
  );
}

// A round HUD icon button overlaid on the video.
function HudButton({ icon, label, onClick, active, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      aria-label={label}
      className={`flex h-9 w-9 items-center justify-center rounded-full border backdrop-blur-md transition ${
        active ? "border-[#00D632]/60 bg-[#00D632]/15 text-[#00D632]" : "border-white/15 bg-black/55 text-white/75 hover:border-white/40 hover:text-white"
      }`}
    >
      <Icon name={icon} size={16} />
    </button>
  );
}

// One seat marker on the felt (open / other player / you).
function SeatMarker({ seat, table, onSit, phase }) {
  const active = table?.status === "acting" && table.active_seat === seat.seat;
  const num = seat.seat + 1;

  if (!seat.occupied) {
    return (
      <button
        type="button"
        data-testid={`bjv2-sit-${seat.seat}`}
        onClick={() => onSit(seat.seat)}
        className="group flex min-h-[64px] w-full flex-col items-center justify-center rounded-2xl border-2 border-dashed border-[#00D632]/45 bg-black/35 px-1 py-2 text-center shadow-[0_0_18px_-4px_rgba(0,214,50,0.5)] backdrop-blur-md transition hover:border-[#00D632] hover:bg-[#00D632]/10"
      >
        <span className="flex h-6 w-6 items-center justify-center rounded-full border border-[#00D632]/60 text-[#00D632]"><Icon name="refer" size={13} /></span>
        <span className="mt-1 text-[9px] font-black uppercase tracking-wide text-[#00D632]">Sit</span>
        <span className="text-[8px] text-white/40">seat {num}</span>
      </button>
    );
  }

  const hand = seat.hands?.[0];
  const totalBet = (seat.hands || []).reduce((s, h) => s + Number(h.bet || 0), 0);
  const result = handResultLabel(hand);
  const decision = decisionLabel(hand, active, null);
  const name = seat.is_me ? "YOU" : `Player ••${String(seat.username || "").replace(/[^a-z0-9]/gi, "").slice(-2).padStart(2, "0")}`;

  // What the seat shows: bet during betting, total once cards are out. F01: BUST
  // appears only for a genuinely busted hand — a non-bust loss keeps its hand value.
  let primary = "";
  if (phase === "betting") primary = totalBet > 0 ? `$${totalBet.toFixed(0)}` : "";
  else if (hand?.status === "bust" || decision === "BUST") primary = "BUST";
  else if (hand?.cards?.length) primary = String(hand.value ?? "");

  return (
    <div
      data-testid={`bjv2-seat-${seat.seat}`}
      className={`flex min-h-[64px] w-full flex-col items-center justify-center rounded-2xl border px-1 py-2 text-center backdrop-blur-md transition-all ${
        active ? "border-[#00D632] bg-[#00D632]/25 shadow-[0_0_18px_-4px_rgba(0,214,50,0.6)]" :
        seat.is_me ? "border-[#00D632]/70 bg-[#00D632]/12" : "border-white/15 bg-black/55"
      }`}
    >
      <span className={`max-w-full truncate text-[9px] font-black ${seat.is_me ? "text-[#00D632]" : "text-white/85"}`}>{name}</span>
      {primary ? (
        <span className={`mt-0.5 font-display text-base font-black leading-none tabular-nums ${primary === "BUST" ? "text-[#EF4444]" : seat.is_me ? "text-[#00D632]" : "text-white"}`}>{primary}</span>
      ) : (
        <span className="mt-0.5 text-[9px] text-white/40">seat {num}</span>
      )}
      {result && phase !== "betting" && (
        <span className={`mt-0.5 truncate rounded px-1 text-[7px] font-black uppercase ${result.startsWith("WIN") || result.startsWith("BLACKJACK") ? "text-[#00D632]" : result === "PUSH" ? "text-white/60" : "text-[#EF4444]"}`}>{result.split(" ")[0]}</span>
      )}
    </div>
  );
}

function commandOptions() {
  const id = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return { headers: { "X-Command-ID": id } };
}

export default function LiveBlackjackV2({ onResult, initialTable = null }) {
  const { user, refresh } = useAuth();
  const [room, setRoom] = useState(null);
  const [lobby, setLobby] = useState({});
  const [bet, setBet] = useState(5);
  const [sideAmt, setSideAmt] = useState("1");
  const [mode, setMode] = useState("player");  // Breakout: Player (manual) | Dealer (auto-play)
  const [currency, setCurrency] = useState("cash");
  const [lastStake, setLastStake] = useState(0);
  const [lastWin, setLastWin] = useState(null);
  const [commandBusy, setCommandBusy] = useState(false);
  const [clockTick, setClockTick] = useState(Date.now());
  // immersive HUD-only UI state (no game/money effect)
  const [muted, setMuted] = useState(true);
  const [videoHealth, setVideoHealth] = useState("offline");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const settledRef = useRef(null);
  const { state: table, setState, transport, secondsLeft, lastUpdateAt, loadError } = useBlackjackTable(room);

  useEffect(() => {
    const id = setInterval(() => setClockTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (room) return;
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/casino/bj/tables");
        if (alive) setLobby(Object.fromEntries(data.map((row) => [row.table_id, row])));
      } catch {}
    };
    load();
    const id = setInterval(load, 5000);
    return () => { alive = false; clearInterval(id); };
  }, [room]);

  // A dedicated discovery tile (e.g. the Breakout tile in the casino Live Dealers shelf) can open
  // this same host with a table preselected (plan §5.3). Jump straight into that room once the
  // catalog confirms it exists and is visible; otherwise fall back to the normal table picker.
  const initialRoomRef = useRef(false);
  useEffect(() => {
    if (!initialTable || room || initialRoomRef.current) return;
    const t = lobby[initialTable];
    if (t && t.visible !== false) {
      initialRoomRef.current = true;
      setBet(Number(t.min_bet ?? 5));
      settledRef.current = null;
      setRoom(initialTable);
    }
  }, [initialTable, room, lobby]);

  useEffect(() => {
    if (!table || table.status !== "settled" || settledRef.current === table.round_number) return;
    settledRef.current = table.round_number;
    // last win = my NET winnings this round (returns − stakes; payouts include the
    // returned stake), so it matches the balance delta and the design's "+$X · Added
    // to …" payout badge (e.g. a $25 blackjack pays $62.50 back → shows +$37.50).
    const seat = table.my_seat != null ? table.seats?.[table.my_seat] : null;
    if (seat) {
      // F03: prefer the server's authoritative complete round total (it aggregates every split
      // hand, push, surrender, insurance and side bet). Fall back to a local calc only if an
      // older server payload omits `settlement`.
      let net;
      if (seat.settlement) {
        net = Number(seat.settlement.net || 0);
      } else {
        const handWin = (seat.hands || []).reduce((s, h) => s + (["win", "blackjack"].includes(h.result) ? Number(h.payout || 0) : 0), 0);
        const sideWin = Object.values(seat.side_results || {}).reduce((s, r) => s + Number(r?.payout || 0), 0);
        const handStake = (seat.hands || []).reduce((s, h) => s + Number(h.bet || 0), 0);
        const sideStake = Object.values(seat.side_bets || {}).reduce((s, v) => s + Number(v || 0), 0);
        net = (handWin + sideWin) - (handStake + sideStake);
      }
      setLastWin(net > 0 ? net : null);
    }
    refresh();
    onResult?.();
  }, [onResult, refresh, table]);

  const releaseAfterVisibleState = (data) => {
    const delay = visibleStateDelayMs(data);
    window.setTimeout(() => setCommandBusy(false), delay + 250);
  };

  const call = async (path, body = {}) => {
    if (stateStale) {
      toast.error("Live table connection is stale — actions are paused until it recovers");
      return null;
    }
    if (commandBusy) return null;
    setCommandBusy(true);
    try {
      const { data } = await api.post(`${path}?table=${room}`, body, commandOptions());
      setState(data);
      refresh();
      releaseAfterVisibleState(data);
      return data;
    } catch (e) {
      setCommandBusy(false);
      toast.error(apiError(e.response?.data?.detail));
      return null;
    }
  };

  const onSit = (seat) => call("/casino/bj/join", { seat });

  const placeBet = async (amount) => {
    if (stateStale) return toast.error("Live table connection is stale — betting is paused until it recovers");
    if (commandBusy) return;
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0) return toast.error("Enter a valid bet");
    setCommandBusy(true);
    try {
      const body = { amount: amt, currency, ...(table?.breakout_mode ? { mode } : {}) };
      const { data } = await api.post(`/casino/bj/bet?table=${room}`, body, commandOptions());
      setState(data);
      setLastStake(amt);
      refresh();
      releaseAfterVisibleState(data);
    } catch (e) {
      setCommandBusy(false);
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const placeSideBet = async (kind, amount) => {
    if (stateStale) return toast.error("Live table connection is stale — betting is paused until it recovers");
    if (commandBusy) return;
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0) return toast.error("Enter a valid side bet");
    setCommandBusy(true);
    try {
      const { data } = await api.post(`/casino/bj/sidebet?table=${room}`, { bet: kind, amount: amt }, commandOptions());
      setState(data);
      refresh();
      releaseAfterVisibleState(data);
    } catch (e) {
      setCommandBusy(false);
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  // ---------------------------------------------------------------- LOBBY (table select)
  if (!room) {
    const anyLive = Object.values(lobby).some((t) => t?.live);
    // F04: the server-owned catalog (/casino/bj/tables) is the single source of truth for
    // which rooms exist and how they present. We render only `visible` tables, preserving
    // the current player-facing set (main + vip). LIVE_BLACKJACK_TABLES is a first-paint
    // fallback used only until the server response arrives.
    const serverRooms = Object.values(lobby).filter((t) => t && t.visible !== false);
    const rooms = serverRooms.length
      ? serverRooms.map((t) => ({ id: t.table_id, label: t.name, min: t.min_bet, rules: t.rules, image: t.image }))
      : LIVE_BLACKJACK_TABLES;
    return (
      <div className="space-y-5" data-testid="bjv2-lobby">
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.3em] text-[#EF4444]">PRYMEBET LIVE</p>
          <h2 className="font-display text-2xl font-black tracking-tight">Live Blackjack</h2>
          <p className="mt-1 text-sm text-white/50">Take a seat at a table. A table shows <span className="font-semibold text-[#EF4444]">Live</span> when a dealer is broadcasting — otherwise it's offline.</p>
        </div>
        {!anyLive && (
          <div data-testid="bjv2-all-offline" className="flex items-center gap-2.5 rounded-2xl border border-white/10 bg-[#141417] px-4 py-3 text-sm text-white/60">
            <span className="h-2 w-2 shrink-0 rounded-full bg-white/25" />
            No tables are live right now — our dealers are offline. You can still take a seat, and the table goes live the moment a dealer starts streaming.
          </div>
        )}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {rooms.map((tb, index) => {
            const info = lobby[tb.id];
            const occupied = info?.seated ?? 0;
            const capacity = info?.seat_count ?? 7;
            return (
              <motion.button
                key={tb.id}
                data-testid={`bjv2-table-${tb.id}`}
                onClick={() => { setBet(info?.min_bet ?? tb.min); settledRef.current = null; setRoom(tb.id); }}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.04 }}
                className="group overflow-hidden rounded-2xl border border-white/10 bg-[#141417] text-left transition hover:-translate-y-0.5 hover:border-[#00D632]/50"
              >
                <div className="relative h-40 overflow-hidden">
                  <img src={tb.image} alt="" className="absolute inset-0 h-full w-full object-cover transition duration-500 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-black/20" />
                  {info?.live ? (
                    <span data-testid={`bjv2-live-${tb.id}`} className="absolute left-3 top-3 flex items-center gap-1 rounded-full bg-[#EF4444] px-2.5 py-1 text-[9px] font-black uppercase tracking-wider text-white">
                      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" /> Live
                    </span>
                  ) : (
                    <span data-testid={`bjv2-offline-${tb.id}`} className="absolute left-3 top-3 rounded-full bg-black/60 px-2.5 py-1 text-[9px] font-black uppercase tracking-wider text-white/55 backdrop-blur">Offline</span>
                  )}
                  <span className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-black/60 px-2.5 py-1 text-[9px] font-bold text-white backdrop-blur">
                    <Icon name="refer" size={12} /> {occupied}/{capacity}
                  </span>
                </div>
                <div className="flex items-center justify-between p-4">
                  <div>
                    <p className="font-display font-black text-white">{info?.name || tb.label}</p>
                    <p className="mt-0.5 text-[11px] text-white/45">{tb.rules}</p>
                  </div>
                  <span className="rounded-lg bg-[#00D632]/10 px-2.5 py-1 text-sm font-black text-[#00D632]">${info?.min_bet ?? tb.min} min</span>
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------- derived (preserved)
  const stateStale = Boolean(room && table && lastUpdateAt && clockTick - lastUpdateAt > 10000);
  const seated = table?.my_seat != null;
  const mySeat = seated ? table.seats?.[table.my_seat] : null;
  const myTurn = Boolean(table && table.status === "acting" && table.active_seat === table.my_seat && !table.awaiting);
  const canBet = Boolean(table && table.status === "betting" && seated && Number(mySeat?.hands?.[0]?.bet || 0) <= 0);
  const canSide = Boolean(table && table.status === "betting" && seated && Number(mySeat?.hands?.[0]?.bet || 0) > 0);
  const needInsurance = Boolean(table?.status === "insurance" && seated && Number(mySeat?.hands?.[0]?.bet || 0) > 0 && !mySeat?.ins_decided);
  const busy = commandBusy || stateStale;

  const phase = table?.status === "betting" || table?.status === "waiting" ? "betting" : "play";
  const balance = currency === "bonus" ? Number(user?.bonus || 0) : Number(user?.cash || 0);
  const minBet = Number(table?.min_bet || 1);
  const maxBet = Number(table?.max_bet || 250);
  const myBet = Number(mySeat?.hands?.[0]?.bet || 0);
  const mySideTotal = Object.values(mySeat?.side_bets || {}).reduce((s, v) => s + Number(v || 0), 0);

  // Breakout-only UI: the four side bets + a Player/Dealer selector. Non-breakout tables keep
  // their existing 21+3 / Perfect Pairs controls unchanged.
  const isBreakout = Boolean(table?.breakout_mode);
  const breakoutPays = table?.breakout_paytables || null;
  const sideBetDefs = isBreakout
    ? [{ key: "breakout_bonus", label: "Breakout Bonus" }, { key: "super_tie", label: "Super Tie" },
       { key: "pair_up_plus", label: "Pair Up Plus" }, { key: "sweet_17", label: "Sweet 17" }]
    : [{ key: "tp3", label: "21+3" }, { key: "pp", label: "Perfect Pairs" }];
  const sidePayLabel = (key) => {
    if (!breakoutPays) return "Paytable TBD";
    if (key === "sweet_17") return `${breakoutPays.sweet_17?.pays}:1`;
    let odds = [];
    if (key === "pair_up_plus") odds = Object.values(breakoutPays.pair_up_plus || {});
    else if (key === "super_tie") odds = Object.values(breakoutPays.super_tie || {}).map((v) => v?.odds);
    else if (key === "breakout_bonus") odds = (breakoutPays.breakout_bonus || []).map((v) => v?.odds);
    const nums = odds.map(Number).filter(Number.isFinite);
    return nums.length ? `up to ${Math.max(...nums)}:1` : "—";
  };
  // §6.4 / §9.5: Breakout Bonus + Super Tie require a Dealer main wager. Use the locked seat mode
  // once a main bet is placed, else the live draft selection.
  const sideEligible = (key) => {
    if (!isBreakout) return true;
    const elig = breakoutPays?.eligibility?.[key];
    const m = mySeat?.mode || mode;
    return elig ? !!elig[m] : true;
  };

  // Top-center status pill
  const status = (() => {
    if (stateStale) return { label: "Connection stale", sub: "actions paused", tone: "red" };
    // Distinguish table loading / unavailable (no state yet) from an in-session
    // "waiting for next round" (state present, status between rounds).
    if (!table) {
      return loadError
        ? { label: "Table unavailable", sub: "retrying…", tone: "red" }
        : { label: "Loading table…", sub: "", tone: "neutral" };
    }
    switch (table?.status) {
      case "betting": return { label: "Place your bets", sub: secondsLeft != null ? `closes in ${secondsLeft}s` : "", tone: "green" };
      case "insurance": return { label: "Insurance", sub: "dealer shows ace", tone: "green" };
      case "dealing": return { label: "Dealing", sub: "cards are being dealt", tone: "neutral" };
      case "acting": return myTurn
        ? { label: "Decision time", sub: `seat ${table.my_seat + 1}${secondsLeft != null ? ` · ${secondsLeft}s` : ""}`, tone: "green" }
        : { label: "Players in action", sub: table.active_seat != null ? `seat ${table.active_seat + 1}` : "", tone: "neutral" };
      case "dealer": return { label: "Dealer hand", sub: table.dealer_value ? `total ${table.dealer_value}` : "", tone: "neutral" };
      case "settled": return { label: "Round complete", sub: "results shown on each seat", tone: "neutral" };
      default: return { label: "Waiting for dealer", sub: "", tone: "neutral" };
    }
  })();

  const connLabel = videoHealth === "live" ? "Connected" : videoHealth === "connecting" || videoHealth === "reconnecting" ? "Connecting" : "Offline";
  const connDot = videoHealth === "live" ? "bg-[#00D632]" : videoHealth === "connecting" || videoHealth === "reconnecting" ? "bg-amber-400" : "bg-zinc-500";

  // dealer felt content
  const dealerCards = table?.dealer || [];
  const dealerLine = (table?.status === "dealer" || table?.status === "settled")
    ? `Dealer total ${table.dealer_value}`
    : dealerCards.length ? `Dealer shows ${dealerCards[0]?.rank || "?"}` : "Dealer · waiting for bets";

  const pillTone = { green: "border-[#00D632]/40 bg-[#00D632]/12 text-[#00D632]", red: "border-[#EF4444]/50 bg-[#EF4444]/15 text-[#EF4444]", neutral: "border-white/15 bg-black/60 text-white/80" };

  // Portal to <body> so the immersive table escapes the Shell's z-10 <main> stacking
  // context and truly covers the viewport (over the top bar / sidebar).
  return createPortal(
    <div className="fixed inset-0 z-[60] flex flex-col bg-[#0A0A0A] text-white" data-testid="bjv2-table">
      {/* ============================= HERO (video + HUD + seats) ============================= */}
      <div className="relative min-h-0 flex-1 overflow-hidden" data-testid="bjv2-stage">
        <LiveDealerVideo
          config={{ stream_url: table?.stream_url, video_provider: table?.video_provider, fallback_stream_url: table?.fallback_stream_url }}
          chrome={false}
          muted={muted}
          onHealth={setVideoHealth}
          className="h-full w-full"
        />
        {/* graceful offline/connecting overlay over the empty feed (hidden once live) */}
        {videoHealth !== "live" && (
          <div className="pointer-events-none absolute inset-0 z-[1] flex items-center justify-center bg-[radial-gradient(circle_at_50%_38%,#16241a_0%,#0b120d_55%,#050705_100%)]" data-testid="bjv2-video-offline">
            <div className="text-center">
              <p className="text-[10px] font-black uppercase tracking-[0.4em] text-white/25">PRYMEBET LIVE</p>
              <p className="mt-2 text-sm font-bold text-white/45">
                {videoHealth === "connecting" || videoHealth === "reconnecting" ? "Connecting to the live dealer…" : "Waiting for the live dealer stream"}
              </p>
            </div>
          </div>
        )}
        {/* soft vignette over the feed so the frame reads like a physical table (dark inset, no glow) */}
        <div className="pointer-events-none absolute inset-0 z-[2] shadow-[inset_0_0_120px_40px_rgba(0,0,0,0.55)]" data-testid="bjv2-video-vignette" />
        {/* top scrim for HUD legibility + bottom gradient blend into the tray (smooth multi-step, no visible band) */}
        <div className="pointer-events-none absolute inset-x-0 top-0 z-10 h-32 bg-gradient-to-b from-black/60 to-transparent" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-1/2 bg-gradient-to-b from-transparent via-[#0A0A0A]/45 via-70% to-[#0A0A0A]" />

        {/* HUD top-left: table name/limits · LIVE · connection */}
        <div className="absolute left-3 top-3 z-20 flex flex-wrap items-center gap-1.5 sm:left-5 sm:top-5">
          <span className="flex items-center gap-1.5 rounded-full border border-white/15 bg-black/60 px-2.5 py-1 text-[11px] font-black text-white backdrop-blur-md">
            <Icon name="blackjack" size={13} className="text-[#00D632]" /> {table?.name || "Live Blackjack"}
            <span className="text-white/40">· ${minBet.toFixed(0)}–${maxBet.toFixed(0)}</span>
          </span>
          <span className="hidden items-center gap-1.5 rounded-full border border-white/10 bg-black/60 px-2 py-1 text-[10px] font-black uppercase tracking-[0.2em] text-white backdrop-blur-md sm:flex">
            <span className="flex h-3 w-3 items-center justify-center rounded-full border border-[#EF4444]">
              <span className="h-1 w-1 rounded-full bg-[#EF4444]" />
            </span> Live
          </span>
          <span className="flex items-center gap-1.5 rounded-full border border-white/10 bg-black/60 px-2 py-1 text-[10px] font-bold text-white/70 backdrop-blur-md" data-testid="bjv2-connection">
            <span className={`h-1.5 w-1.5 rounded-full ${connDot}`} /> <span className="hidden sm:inline">{connLabel}</span>
          </span>
        </div>

        {/* HUD top-right: sound / settings / info / lobby */}
        <div className="absolute right-3 top-3 z-30 flex items-center gap-1.5 sm:right-5 sm:top-5">
          <HudButton icon={muted ? "muted" : "sound-on"} label={muted ? "Unmute" : "Mute"} onClick={() => setMuted((m) => !m)} testid="bjv2-mute" />
          <HudButton icon="settings" label="Settings" onClick={() => { setSettingsOpen((v) => !v); setInfoOpen(false); }} active={settingsOpen} testid="bjv2-settings-btn" />
          <span className="hidden sm:block"><HudButton icon="info" label="Table info" onClick={() => { setInfoOpen((v) => !v); setSettingsOpen(false); }} active={infoOpen} testid="bjv2-info-btn" /></span>
          <button
            type="button"
            data-testid="bjv2-lobby-back"
            onClick={() => setRoom(null)}
            className="flex h-9 items-center gap-1.5 rounded-full border border-white/15 bg-black/55 px-2.5 text-xs font-bold text-white/80 backdrop-blur-md transition hover:border-white/40 hover:text-white"
          >
            <Icon name="arrow-left" size={15} /> <span className="hidden sm:inline">Lobby</span>
          </button>
        </div>

        {/* top-center status pill */}
        <div className="pointer-events-none absolute left-1/2 top-14 z-20 flex max-w-[90vw] -translate-x-1/2 sm:top-5 sm:max-w-none">
          <span className={`flex items-center gap-1.5 truncate rounded-full border px-3 py-1.5 text-xs font-black backdrop-blur-md ${pillTone[status.tone]}`} data-testid="bjv2-status">
            <Icon name="clock" size={13} className="shrink-0" /> {status.label}{status.sub ? <span className="font-bold text-white/55">· {status.sub}</span> : null}
          </span>
        </div>

        {/* settings / info popovers */}
        {settingsOpen && (
          <div className="absolute right-3 top-14 z-40 w-56 rounded-2xl border border-white/10 bg-[#0d0d10]/95 p-3 text-left shadow-2xl backdrop-blur-xl sm:right-5 sm:top-16" data-testid="bjv2-settings-panel">
            <p className="mb-2 text-[10px] font-black uppercase tracking-wider text-white/40">Currency</p>
            <div className="flex gap-2">
              <button disabled={myBet > 0} onClick={() => setCurrency("cash")} className={`flex-1 rounded-lg border px-2 py-1.5 text-xs font-bold disabled:opacity-40 ${currency === "cash" ? "border-[#00D632] bg-[#00D632]/15 text-[#00D632]" : "border-white/15 text-white/70"}`}>
                <Icon name="pnrcash" size={12} className="mr-1 inline text-pnr-cash" />PCash
              </button>
              {(user?.bonus || 0) > 0 && (
                <button disabled={myBet > 0} onClick={() => setCurrency("bonus")} className={`flex-1 rounded-lg border px-2 py-1.5 text-xs font-bold disabled:opacity-40 ${currency === "bonus" ? "border-[#00D632] bg-[#00D632]/15 text-[#00D632]" : "border-white/15 text-white/70"}`}>Bonus</button>
              )}
            </div>
            {myBet > 0 && <p className="mt-2 text-[9px] text-white/35">Locked while a bet is live.</p>}
          </div>
        )}
        {infoOpen && (
          <div className="absolute right-3 top-14 z-40 w-64 rounded-2xl border border-white/10 bg-[#0d0d10]/95 p-3 text-left text-[11px] leading-relaxed text-white/70 shadow-2xl backdrop-blur-xl sm:right-5 sm:top-16" data-testid="bjv2-info-panel">
            <p className="font-black text-white">{table?.name}</p>
            <p className="mt-1">Round {table?.round_number ?? "—"} · {table?.decks ?? 6} decks · dealer stands soft 17</p>
            <p className="mt-1">Limits ${minBet.toFixed(0)}–${maxBet.toFixed(0)}. {table?.push_22 ? "Double on any number of cards · dealer 22 pushes standing hands." : ""}</p>
            <p className="mt-1 text-white/45">Cards render in sync with the live video and are never revealed ahead of it.</p>
          </div>
        )}

        {/* dealer felt (upper-center) */}
        <div className="absolute left-1/2 top-28 z-20 flex -translate-x-1/2 flex-col items-center sm:top-20" data-testid="bjv2-dealer">
          <div className="flex items-center justify-center gap-1.5">
            {dealerCards.length ? (
              dealerCards.map((c, i) => <PlayingCard key={i} card={c} />)
            ) : (
              <>
                <span className="h-12 w-8 rounded-md border-2 border-dashed border-white/25" />
                <span className="h-12 w-8 rounded-md border-2 border-dashed border-white/25" />
              </>
            )}
          </div>
          <span className="mt-1.5 rounded-full bg-black/60 px-2.5 py-1 text-[10px] font-bold text-white/80 backdrop-blur-md">{dealerLine}</span>
        </div>

        {/* seat arc (lower felt) */}
        <div className="absolute inset-x-0 bottom-3 z-20 px-2 sm:bottom-5 sm:px-6" data-testid="bjv2-seats">
          <div className="mx-auto flex max-w-5xl items-end justify-center gap-1.5 sm:gap-2.5">
            {table?.seats?.map((seat, i) => {
              // gentle arc: outer seats sit a touch higher than the centre
              const arc = Math.abs(i - (table.seats.length - 1) / 2);
              return (
                <div key={seat.seat} className="min-w-0 flex-1" style={{ transform: `translateY(-${Math.round(arc * 5)}px)` }}>
                  <SeatMarker seat={seat} table={table} onSit={onSit} phase={phase} />
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ============================= CONTROL TRAY (frosted glass) ============================= */}
      {/* Pulled up (-mt) so the video hero runs slightly under the frosted slab; the "Your Hand"
          band (first child) straddles this top edge in the blend zone without obscuring the feed. */}
      <div
        className="relative z-40 -mt-5 shrink-0 rounded-t-3xl border-t border-white/10 bg-[#0A0A0A]/72 px-3 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] pt-2 shadow-[0_-14px_40px_-10px_rgba(0,0,0,0.75)] backdrop-blur-xl sm:mx-3.5 sm:mb-3.5 sm:-mt-6 sm:rounded-3xl sm:px-6 sm:pt-3"
        data-testid="bjv2-tray"
      >
        {/* NON-OBSTRUCTING "Your Hand" band — rests on the tray rim, overlapping the top edge. */}
        <YourHandBand
          seated={seated} mySeat={mySeat} table={table} phase={phase} canBet={canBet} myTurn={myTurn}
          myBet={myBet} mySideTotal={mySideTotal} secondsLeft={secondsLeft} lastWin={lastWin}
          currency={currency} lastStake={lastStake} placeBet={placeBet} busy={busy}
        />

        {stateStale && (
          <div className="mx-auto mb-2 max-w-6xl rounded-xl border-2 border-[#EF4444]/45 bg-[#EF4444]/15 px-3 py-1.5 text-center text-[11px] font-black text-[#EF4444]" data-testid="bjv2-stale-warning">
            LIVE TABLE CONNECTION IS STALE — BETTING AND ACTIONS ARE PAUSED UNTIL STATE RECOVERS
          </div>
        )}

        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-2.5 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.35fr)_minmax(0,0.85fr)]">
          {/* -------- LEFT: currency + balance + last win + your bets summary -------- */}
          <div className="rounded-2xl border border-white/10 bg-[#111114]/90 p-3">
            <div className="flex items-center gap-1.5">
              <button disabled={myBet > 0} onClick={() => setCurrency("cash")} data-testid="bjv2-cur-cash"
                className={`flex flex-1 items-center justify-center gap-1 rounded-lg border px-2 py-1.5 text-[11px] font-black disabled:opacity-50 ${currency === "cash" ? "border-[#00D632] bg-[#00D632] text-[#0A0A0A]" : "border-white/15 text-white/60"}`}>
                <Icon name="pnrcash" size={13} className={currency === "cash" ? "text-[#0A0A0A]" : "text-pnr-cash"} /> PCash
              </button>
              <button disabled={myBet > 0 || (user?.bonus || 0) <= 0} onClick={() => setCurrency("bonus")} data-testid="bjv2-cur-bonus"
                className={`flex flex-1 items-center justify-center gap-1 rounded-lg border px-2 py-1.5 text-[11px] font-black disabled:opacity-40 ${currency === "bonus" ? "border-[#00D632] bg-white/10 text-[#00D632]" : "border-white/15 text-white/60"}`}>
                <Icon name="gem" size={12} /> Bonus
              </button>
            </div>
            <div className="mt-2 flex items-end justify-between">
              <div>
                <p className="text-[9px] font-black uppercase tracking-[0.18em] text-white/40">Balance</p>
                <p className="font-display text-xl font-black tabular-nums lg:text-3xl lg:tracking-tight">${balance.toFixed(2)}</p>
              </div>
              <div className="text-right">
                <p className="text-[9px] font-black uppercase tracking-[0.18em] text-white/40">Last win</p>
                <p className={`font-display text-sm font-black tabular-nums ${lastWin ? "text-[#00D632]" : "text-white/50"}`}>{lastWin != null ? `+$${lastWin.toFixed(2)}` : "—"}</p>
              </div>
            </div>
            {/* SEAT N · YOUR BETS summary (replaces the old hand box; the hand now lives in the band). */}
            <div className="mt-2 rounded-xl border border-white/10 bg-black/30 p-2" data-testid="bjv2-yourbets">
              <p className="text-[9px] font-black uppercase tracking-[0.18em] text-white/40">
                {seated ? `Seat ${table.my_seat + 1} · your bets` : "Your bets"}
              </p>
              {seated ? (
                <>
                  <div className="mt-1.5 flex items-center gap-2.5">
                    <ChipStack label={myBet > 0 ? `$${myBet.toFixed(0)}` : ""} />
                    <div className="leading-none">
                      <span className="font-display text-lg font-black tabular-nums text-white">${myBet.toFixed(2)}</span>
                      {mySideTotal > 0 && (
                        <span className="ml-1.5 text-[10px] font-black text-white/50">+ ${mySideTotal.toFixed(2)} side</span>
                      )}
                    </div>
                  </div>
                  <p className="mt-1.5 text-[9px] font-bold text-white/35">
                    {myTurn ? "If you split, the second hand waits here" : "Split hands queue here"}
                  </p>
                </>
              ) : (
                <p className="mt-1 text-xs font-bold text-white/40">Pick a seat to play</p>
              )}
            </div>
          </div>

          {/* -------- CENTER: betting / decision / insurance / status -------- */}
          <div className="flex min-h-[120px] flex-col justify-center rounded-2xl border border-[#00D632]/30 bg-[#0c130e]/90 p-3" data-testid="bjv2-center">
            {!seated ? (
              <p className="text-center text-sm font-bold text-white/60">Choose any open <span className="text-[#00D632]">green seat</span> on the live table to join.</p>
            ) : needInsurance ? (
              <div className="text-center">
                <p className="font-black text-[#00D632]">{mySeat?.hands?.[0]?.status === "blackjack" ? "Take even money?" : "Dealer shows an Ace — take insurance?"}</p>
                <p className="mt-0.5 text-[10px] font-bold text-white/45">
                  {mySeat?.hands?.[0]?.status === "blackjack" ? "Even money locks a 1:1 win now." : `Insurance costs $${(myBet * 0.5).toFixed(2)} and pays 2:1 if the dealer has blackjack.`}
                </p>
                <div className="mt-2.5 flex justify-center gap-2">
                  <Button disabled={busy} onClick={() => call("/casino/bj/insurance", { action: mySeat?.hands?.[0]?.status === "blackjack" ? "even_money" : "take" })} className="rounded-full bg-white/10 px-6 py-5 font-black text-white hover:bg-white/20 disabled:opacity-40">Yes</Button>
                  <Button disabled={busy} onClick={() => call("/casino/bj/insurance", { action: "no" })} className="rounded-full bg-[#00D632] px-6 py-5 font-black text-[#0A0A0A] hover:bg-[#00B829] disabled:opacity-40">No</Button>
                </div>
              </div>
            ) : myTurn ? (
              <DecisionControls table={table} mySeat={mySeat} call={call} busy={busy} secondsLeft={secondsLeft} />
            ) : canBet ? (
              <div className="space-y-2.5">
                {isBreakout && (
                  <div data-testid="bjv2-mode-select" className="rounded-xl border border-[#00D632]/25 bg-black/30 p-2">
                    <p className="text-[9px] font-black uppercase tracking-[0.18em] text-white/40">Play this seat as</p>
                    <div className="mt-1.5 grid grid-cols-2 gap-2">
                      {[{ k: "player", t: "Player", d: "You decide (hit/stand/split…)" }, { k: "dealer", t: "Dealer", d: "Auto-plays by dealer rules" }].map((m) => (
                        <button
                          key={m.k}
                          type="button"
                          data-testid={`bjv2-mode-${m.k}`}
                          onClick={() => setMode(m.k)}
                          className={`rounded-lg border px-2 py-1.5 text-left transition ${mode === m.k ? "border-[#00D632] bg-[#00D632]/15" : "border-white/15 bg-black/30 hover:border-[#00D632]/50"}`}
                        >
                          <span className={`block text-[11px] font-black ${mode === m.k ? "text-[#00D632]" : "text-white"}`}>{m.t}</span>
                          <span className="block text-[8px] leading-tight text-white/45">{m.d}</span>
                        </button>
                      ))}
                    </div>
                    {mode === "dealer" && (
                      <p data-testid="bjv2-autoplay-note" className="mt-1.5 text-[9px] font-bold leading-snug text-[#00D632]/80">Dealer mode: your hand plays automatically by dealer rules — no hit/stand, split, double, surrender or insurance.</p>
                    )}
                  </div>
                )}
                <BettingControls table={table} bet={bet} setBet={setBet} minBet={minBet} maxBet={maxBet} placeBet={placeBet} lastStake={lastStake} busy={busy} secondsLeft={secondsLeft} mySideTotal={mySideTotal} />
              </div>
            ) : canSide ? (
              <div className="text-center">
                <p className="font-black text-[#00D632]">Main bet locked · ${myBet.toFixed(2)}</p>
                {isBreakout && mySeat?.mode && (
                  <span data-testid="bjv2-mode-badge" className="mt-1 inline-block rounded-full border border-[#00D632]/40 bg-[#00D632]/10 px-2 py-0.5 text-[9px] font-black uppercase tracking-wider text-[#00D632]">{mySeat.mode === "dealer" ? "Dealer · auto-play" : "Player"}</span>
                )}
                <p className="mt-0.5 text-[10px] font-bold text-white/45">Add optional side bets on the right{secondsLeft != null ? ` · closes in ${secondsLeft}s` : ""}.</p>
              </div>
            ) : (
              <p className="text-center text-sm font-bold text-white/50">
                {table?.status === "acting" && table.awaiting?.seat === table.my_seat ? "Dealer is drawing your requested card…" :
                 table?.status === "acting" ? "Watch the live table — waiting for your turn." :
                 table?.status === "dealing" ? "Opening cards are being dealt." :
                 table?.status === "dealer" ? "Dealer is completing the hand." :
                 table?.status === "settled" ? "Round complete — results are shown on each seat." :
                 myBet > 0 ? "Your bet is locked. Waiting for the deal." : "Waiting for the dealer to open betting."}
              </p>
            )}
          </div>

          {/* -------- RIGHT: side bets + rules -------- */}
          <div className="rounded-2xl border border-white/10 bg-[#111114]/90 p-3" data-testid="bjv2-sidebets">
            <p className="text-[9px] font-black uppercase tracking-[0.18em] text-white/40">Side bets</p>
            <div className="mt-1.5 grid grid-cols-2 gap-2">
              {sideBetDefs.map((sb) => {
                const placed = Number(mySeat?.side_bets?.[sb.key] || 0);
                const eligible = sideEligible(sb.key);
                const enabled = canSide && currency === "cash" && placed <= 0 && !busy && eligible;
                return (
                  <button
                    key={sb.key}
                    data-testid={`bjv2-side-${sb.key}`}
                    disabled={!enabled}
                    onClick={() => placeSideBet(sb.key, sideAmt)}
                    className={`flex flex-col items-start rounded-xl border p-2 text-left transition ${placed > 0 ? "border-[#00D632]/60 bg-[#00D632]/10" : enabled ? "border-white/15 bg-black/30 hover:border-[#00D632]/60" : "border-white/10 bg-black/20 opacity-60"}`}
                  >
                    <span className="text-[11px] font-black text-white">{sb.label}</span>
                    <span className="text-[9px] text-white/40">{!eligible ? "Dealer bet required" : isBreakout ? sidePayLabel(sb.key) : "Paytable TBD"}</span>
                    <span className={`mt-1 text-[11px] font-black ${placed > 0 ? "text-[#00D632]" : "text-white/50"}`}>{placed > 0 ? `$${placed.toFixed(2)}` : enabled ? `Add $${Number(sideAmt).toFixed(2)}` : "—"}</span>
                  </button>
                );
              })}
            </div>
            {canSide && currency === "cash" && (
              <div className="mt-2 flex items-center justify-between gap-2">
                <span className="text-[9px] font-black uppercase tracking-wider text-white/40">Side stake</span>
                <div className="flex items-center gap-1">
                  {[1, 5, 10].map((v) => (
                    <button key={v} onClick={() => setSideAmt(String(v))} className={`rounded-md border px-2 py-1 text-[10px] font-black ${Number(sideAmt) === v ? "border-[#00D632] bg-[#00D632]/15 text-[#00D632]" : "border-white/15 text-white/60"}`}>${v}</button>
                  ))}
                </div>
              </div>
            )}
            <p className="mt-2 border-t border-white/10 pt-2 text-[9px] leading-relaxed text-white/40">
              Round {table?.round_number ?? "—"} · {table?.decks ?? 6} decks · dealer stands soft 17{table?.push_22 ? " · 22 push · double any" : ""}
            </p>
          </div>
        </div>

        {seated && ["waiting", "betting", "settled"].includes(table?.status) && (
          <button onClick={() => call("/casino/bj/leave", {})} className="mx-auto mt-2 block text-[11px] font-bold text-white/35 underline underline-offset-4 hover:text-white/70" data-testid="bjv2-leave">
            Leave seat
          </button>
        )}
      </div>
    </div>,
    document.body
  );
}

/* -------- NON-OBSTRUCTING "Your Hand" band --------
   Rests on the tray rim (rendered as the first child of the tray, pulled up to straddle the top
   edge). Three states, all driven by the real seat/hand/settlement data already derived above.
   Purely presentational: the only interactive element is the payout Rebet button, which routes
   through the same placeBet() path as the existing Rebet control. */
function YourHandBand({ seated, mySeat, table, phase, canBet, myTurn, myBet, mySideTotal, secondsLeft, lastWin, currency, lastStake, placeBet, busy }) {
  if (!seated) return null;
  const seatNo = table.my_seat + 1;
  const activeHand = mySeat?.hands?.[table?.active_hand || 0];
  const cards = activeHand?.cards || [];
  const dealt = cards.length > 0;
  const settled = table?.status === "settled";
  const iWon = settled && Number(lastWin || 0) > 0;
  const currencyLabel = currency === "bonus" ? "PCoins" : "PCash";

  // fan angle for the hero cards (~ −4° first, +3° second, spread evenly for more)
  const fanAngle = (i, n) => {
    if (n <= 1) return 0;
    const spread = Math.min(14, 7 * (n - 1));
    return -spread / 2 + (spread / (n - 1)) * i;
  };
  const HeroCards = ({ list }) => (
    <div className="flex items-end -space-x-3 lg:-space-x-4" data-testid="bjv2-yourhand-cards">
      {list.slice(0, 6).map((c, i) => (
        <PlayingCard key={i} card={c} hero style={{ transform: `rotate(${fanAngle(i, Math.min(list.length, 6))}deg)` }} />
      ))}
    </div>
  );
  const DashedSlots = () => (
    <div className="flex items-end gap-2" data-testid="bjv2-yourhand-slots">
      {[0, 1].map((i) => (
        <span key={i} className="flex h-[70px] w-[50px] flex-col items-center justify-center rounded-[9px] border-2 border-dashed border-white/20 text-center text-[8px] font-bold leading-tight text-white/30 lg:h-[134px] lg:w-24 lg:rounded-xl lg:text-[10px]">
          {i === 0 ? "Your cards" : "deal here"}
        </span>
      ))}
    </div>
  );

  // ---------- PAYOUT ----------
  if (iWon) {
    const won = mySeat?.hands?.find((h) => ["win", "blackjack"].includes(h.result)) || activeHand;
    const word = won?.result === "blackjack" ? "Blackjack" : "Win";
    const nextIn = secondsLeft != null ? secondsLeft : null;
    const canRebet = lastStake > 0 && lastStake <= Number(table?.max_bet || Infinity);
    return (
      <BandShell testid="bjv2-payout" tone="win"
        label={<><span className="h-1.5 w-1.5 rounded-full bg-[#00D632]" /> PAID · SEAT {seatNo}</>}>
        <div className="flex flex-col items-center gap-2 lg:flex-row lg:items-center lg:gap-4">
          <HeroCards list={won?.cards || cards} />
          <div className="rounded-2xl border border-[#00D632]/40 bg-black/50 px-4 py-2.5 text-center lg:text-left">
            <p className="font-display text-3xl font-black tabular-nums text-[#00D632] lg:text-5xl">+${Number(lastWin).toFixed(2)}</p>
            <p className="mt-0.5 flex items-center justify-center gap-1 text-xs font-black uppercase tracking-wider text-white/80 lg:justify-start">{word} · Added to <Icon name={currency === "bonus" ? "pnrcoin" : "pnrcash"} size={13} className={currency === "bonus" ? "text-pnr-coin" : "text-pnr-cash"} />{currencyLabel}</p>
            <div className="mt-2 flex items-center justify-center gap-2 lg:justify-start">
              {nextIn != null && <span className="text-[10px] font-bold text-white/45">Next round in {nextIn}s</span>}
              {canRebet && (
                <button
                  type="button"
                  data-testid="bjv2-rebet-win"
                  disabled={busy}
                  onClick={() => placeBet(lastStake)}
                  className="flex items-center gap-1 rounded-full bg-[#00D632] px-3 py-1.5 text-[11px] font-black text-[#0A0A0A] transition hover:bg-[#00B829] disabled:opacity-40"
                >
                  <Icon name="refresh" size={12} /> Rebet ${lastStake.toFixed(0)}
                </button>
              )}
            </div>
          </div>
        </div>
      </BandShell>
    );
  }

  // ---------- DECISION / ACTIVE ----------
  // Only while a hand is actually in play — never during betting/waiting. This stops the
  // previous hand's cards from flashing in the band for a poll cycle when the table
  // auto-advances settled -> next betting round (the seat's cards linger briefly).
  if (dealt && !settled && phase !== "betting") {
    const soft = activeHand?.soft;
    const value = activeHand?.value;
    const dealerUp = table?.dealer?.[0]?.rank;
    return (
      <BandShell testid="bjv2-yourhand" tone="active"
        label={<>
          <span className="h-1.5 w-1.5 rounded-full bg-[#00D632]" /> YOUR MOVE · SEAT {seatNo}
          {myTurn && secondsLeft != null && (
            <span className="ml-1 flex h-4 w-4 items-center justify-center rounded-full border border-[#00D632]/60 text-[8px] font-black tabular-nums text-[#00D632]">{secondsLeft}</span>
          )}
        </>}>
        <div className="flex items-center gap-3 lg:gap-5">
          <HeroCards list={cards} />
          <div className="rounded-2xl border border-white/12 bg-black/50 px-3 py-2 text-center lg:px-4">
            <p className="font-display text-[38px] font-black leading-none tabular-nums text-[#00D632] lg:text-[56px]">{value ?? "—"}</p>
            <div className="my-1 h-px w-full bg-white/10" />
            <p className="text-[11px] font-black text-white/85">{soft ? "Soft " : ""}{value ?? "—"}</p>
            <p className="text-[10px] font-bold text-white/45">vs dealer {dealerUp || "—"}</p>
          </div>
        </div>
      </BandShell>
    );
  }

  // ---------- BETTING (pre-deal: bet placed or betting open) ----------
  const mainLine = `$${Number(myBet).toFixed(0)} main · $${Number(mySideTotal).toFixed(0)} 21+3`;
  return (
    <BandShell testid="bjv2-yourhand" tone="idle"
      label={<><span className="h-1.5 w-1.5 rounded-full bg-white/40" /> YOUR HAND · SEAT {seatNo}</>}>
      <div className="flex items-center gap-3 lg:gap-5">
        <DashedSlots />
        <div className="rounded-2xl border border-white/10 bg-white/[0.04] px-3 py-2 text-center lg:px-4">
          <p className="font-display text-[38px] font-black leading-none text-white/30 lg:text-[56px]">—</p>
          <p className="mt-1 text-[11px] font-black text-white/60">Dealt when betting closes</p>
          {/* desktop: main · 21+3 summary; mobile: shorter deals-at-close line */}
          <p className="hidden text-[10px] font-bold text-white/40 lg:block">{mainLine}</p>
          <p className="text-[10px] font-bold text-white/40 lg:hidden">Deals at close · ${Number(myBet).toFixed(0)} bet</p>
        </div>
      </div>
    </BandShell>
  );
}

// Shared shell for the "Your Hand" band — centered, overlaps the tray rim, dark drop shadow (no glow).
function BandShell({ testid, tone, label, children }) {
  const ring = tone === "win" ? "border-[#00D632]/40" : tone === "active" ? "border-[#00D632]/30" : "border-white/12";
  return (
    <div className="pointer-events-none relative z-10 -mt-[20px] mb-1 flex justify-center lg:-mt-[25px]" data-testid={testid}>
      <div className={`pointer-events-auto flex flex-col items-center gap-1.5 rounded-2xl border ${ring} bg-[#0A0A0A]/85 px-4 py-2.5 shadow-[0_10px_30px_-8px_rgba(0,0,0,0.8)] backdrop-blur-md`}>
        <div className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-[0.18em] text-white/70">{label}</div>
        {children}
      </div>
    </div>
  );
}

// -------- Betting controls (chips, ½/2×, place bet + timer) --------
function BettingControls({ table, bet, setBet, minBet, maxBet, placeBet, lastStake, busy, secondsLeft, mySideTotal }) {
  const chips = [1, 5, 25, 100, 500];
  const clamp = (v) => Math.max(minBet, Math.min(maxBet, v));
  const amount = Number(bet || 0);
  const total = amount + Number(mySideTotal || 0);
  return (
    <div className="space-y-2.5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {secondsLeft != null && (
            <span className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-[#00D632]/60 text-xs font-black tabular-nums text-[#00D632]" data-testid="bjv2-timer">{secondsLeft}</span>
          )}
          <div>
            <p className="text-[10px] font-black uppercase tracking-wider text-[#00D632]">Betting open</p>
            <p className="text-[9px] font-bold text-white/40">Limits ${minBet.toFixed(0)}–${maxBet.toFixed(0)}</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-[9px] font-black uppercase tracking-wider text-white/40">Bet</p>
          <p className="font-display text-lg font-black tabular-nums text-white">${amount.toFixed(2)}</p>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-center gap-1.5">
        {chips.map((chip) => (
          <button
            key={chip}
            disabled={busy}
            onClick={() => setBet((c) => clamp(Number(c || 0) + chip))}
            data-testid={`bjv2-chip-${chip}`}
            style={{
              background: "radial-gradient(circle at 50% 38%, #1cf24d 0%, #00D632 58%, #00a827 100%)",
              boxShadow: "0 2px 5px rgba(0,0,0,0.5), inset 0 0 0 2px rgba(0,0,0,0.18)",
            }}
            className="relative flex h-11 w-11 items-center justify-center rounded-full border border-black/30 text-[11px] font-black text-black transition-transform before:absolute before:inset-[4px] before:rounded-full before:border before:border-dashed before:border-white/70 before:content-[''] hover:scale-110 disabled:opacity-40"
          >
            <span className="relative">${chip}</span>
          </button>
        ))}
      </div>

      <div className="flex items-center justify-center gap-1.5">
        <button disabled={busy} onClick={() => setBet(clamp(Math.floor(amount / 2)))} className="rounded-lg border border-white/15 px-3 py-2 text-xs font-black text-white/70 disabled:opacity-40">½</button>
        <button disabled={busy} onClick={() => setBet(clamp(amount * 2))} className="rounded-lg border border-white/15 px-3 py-2 text-xs font-black text-white/70 disabled:opacity-40">2×</button>
        <button disabled={busy} onClick={() => setBet(minBet)} data-testid="bjv2-undo" className="rounded-lg border border-white/15 px-3 py-2 text-xs font-bold text-white/60 disabled:opacity-40">Undo</button>
        {lastStake > 0 && lastStake <= maxBet && (
          <button disabled={busy} onClick={() => placeBet(lastStake)} data-testid="bjv2-rebet" className="rounded-lg border border-[#00D632]/40 bg-[#00D632]/10 px-3 py-2 text-xs font-black text-[#00D632] disabled:opacity-40">Rebet ${lastStake.toFixed(0)}</button>
        )}
      </div>

      <Button disabled={busy} onClick={() => placeBet(amount)} data-testid="bjv2-place-bet" className="w-full rounded-full bg-[#00D632] py-6 text-sm font-black text-[#0A0A0A] hover:bg-[#00B829] disabled:opacity-40">
        Place bet · ${total.toFixed(2)}
      </Button>
    </div>
  );
}

// -------- Decision controls (Hit / Stand / Double / Split / Surrender) --------
function DecisionControls({ table, mySeat, call, busy, secondsLeft }) {
  const hand = mySeat?.hands?.[table.active_hand || 0];
  const bet = Number(hand?.bet || 0);
  const twoCards = hand?.cards?.length === 2;
  const canDouble = hand && (table.double_any ? hand.cards.length >= 2 : twoCards) && bet * 2 <= Number(table.max_bet || Infinity);
  const canSplit = hand && mySeat.hands.length === 1 && twoCards && hand.cards[0]?.rank === hand.cards[1]?.rank;
  const canSurrender = hand && mySeat.hands.length === 1 && twoCards;
  const act = (action) => call("/casino/bj/action", { action });

  // desktop keyboard shortcuts for the enabled actions (same guarded path as the buttons)
  useEffect(() => {
    const onKey = (e) => {
      if (busy || e.metaKey || e.ctrlKey || e.altKey) return;
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return;
      const k = e.key.toLowerCase();
      if (k === "h") act("hit");
      else if (k === "s") act("stand");
      else if (k === "d" && canDouble) act("double");
      else if (k === "p" && canSplit) act("split");
      else if (k === "r" && canSurrender) act("surrender");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, canDouble, canSplit, canSurrender]); // eslint-disable-line react-hooks/exhaustive-deps

  const soft = hand?.soft;
  const dealerUp = table?.dealer?.[0]?.rank;
  const btn = "flex flex-col items-center justify-center rounded-2xl py-4 font-black transition disabled:cursor-not-allowed";
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-black uppercase tracking-[0.16em] text-[#00D632]">Your move{secondsLeft != null ? ` · ${secondsLeft}s` : ""}</p>
          <p className="mt-0.5 text-[10px] font-bold text-white/45">{soft ? "Soft " : ""}{hand?.value ?? "—"}{dealerUp ? ` against dealer ${dealerUp}` : ""} · Bet ${bet.toFixed(2)}</p>
        </div>
        <span className="rounded-full border border-[#00D632]/40 bg-[#00D632]/10 px-2.5 py-1 text-sm font-black tabular-nums text-[#00D632]">{hand?.value ?? "—"}</span>
      </div>
      <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-5">
        <button disabled={busy} onClick={() => act("hit")} data-testid="bjv2-hit" className={`${btn} col-span-2 bg-[#00D632] text-black hover:bg-[#00B829] sm:col-span-1`}>
          Hit<span className="mt-0.5 hidden text-[9px] font-bold text-black/60 sm:block">Draw · H</span>
        </button>
        <button disabled={busy} onClick={() => act("stand")} data-testid="bjv2-stand" className={`${btn} bg-white/10 text-white hover:bg-white/20`}>
          Stand<span className="mt-0.5 hidden text-[9px] font-bold text-white/45 sm:block">Keep · S</span>
        </button>
        <button disabled={busy || !canDouble} onClick={() => act("double")} data-testid="bjv2-double" className={`${btn} bg-white/10 text-white hover:bg-white/20 disabled:opacity-35`}>
          Double<span className="mt-0.5 hidden text-[9px] font-bold text-white/45 sm:block">{canDouble ? `+$${bet.toFixed(0)} · D` : "2 cards"}</span>
        </button>
        <button disabled={busy || !canSplit} onClick={() => act("split")} data-testid="bjv2-split" className={`${btn} bg-white/10 text-white hover:bg-white/20 disabled:opacity-35`}>
          Split<span className="mt-0.5 hidden text-[9px] font-bold text-white/45 sm:block">{canSplit ? "P" : "Pairs only"}</span>
        </button>
        <button disabled={busy || !canSurrender} onClick={() => act("surrender")} data-testid="bjv2-surrender" className={`${btn} border border-white/15 bg-white/5 text-white hover:bg-white/10 disabled:opacity-35`}>
          Surrender<span className="mt-0.5 hidden text-[9px] font-bold text-white/45 sm:block">{canSurrender ? "R" : "2 cards"}</span>
        </button>
      </div>
    </div>
  );
}
